import i from 'i18next'
import { useContext, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Router } from 'next/router'
import Link from 'next/link'
import useMedia from 'use-media'
import { MenuCollection } from 'lib/hooks/use-collections'
import { State } from 'components/Layout'
import Logo from 'components/Logo'
import Close from 'components/svg/Close'
import Back from 'components/svg/Back'
import Thumbnail from 'components/product/Thumbnail'
import { mobileMenuMaxWidth } from 'constants/styles'
import { headers as ease } from 'constants/easings'
import Title from 'components/collection/Title'

type Props = {
	collections: MenuCollection[]
}

export default function Collections({ collections }: Props ) {
	const { flyout, setFlyout } = useContext( State )
	const popular = collections[ 0 ]
	const [ active, setActive ] = useState<MenuCollection>( popular )
	const mobile = useMedia({ maxWidth: `${mobileMenuMaxWidth}px` })

	// Hide menu when navigating to another (dynamic) page
	const hideMenu = () => setFlyout( false )
	useEffect( () => {
		if ( flyout === 'collections' ) {
			Router.events.on( 'routeChangeComplete', hideMenu )
			return () => Router.events.off( 'routeChangeComplete', hideMenu )
		}
	}, [ flyout ] )

	const hideOnBackdropClick = e => {
		if ( e.target.className === 'backdrop' ) hideMenu()
	}

	return (
		<div className="menu" onClick={hideOnBackdropClick}>
			<motion.div
				initial="hidden"
				animate={( flyout === 'collections' ? 'visible' : 'hidden' )}
				className="backdrop"
			>
				<nav className="cf">
					<motion.div
						className="background"
						variants={{
							hidden: { x: '-100%' },
							visible: { x: '0%' },
						}}
						transition={{ duration: 1, ease }}
					/>
					<motion.div
						transition={{ duration: .5, ease }}
						variants={{
							hidden: { opacity: 0 },
							visible: { opacity: 1, transition: { delay: .25 } },
						}}
					>
						<div>
							<Link href="/" legacyBehavior><a aria-label="Ray Logo"><Logo /></a></Link>
							{mobile && (
								<a
									aria-label="Close Menu"
									className="back"
									onClick={hideMenu}
								>
									<Back />
									{i.t( 'nav.products' )}
								</a>
							)}
							<motion.ul
								className="collections"
								variants={{
									visible: {
										transition: { staggerChildren: .15 },
									},
								}}
							>
								{collections.map( collection => (
									<motion.li
										key={collection.handle}
										variants={{
											hidden: { opacity: 0, y: 80 },
											visible: { opacity: 1, y: 0 },
										}}
										transition={{
											opacity: { duration: 1.5, ease },
											default: { duration: 1, ease },
										}}
									>
										<Link
											href="/products/[collection]"
											as={`/products/${collection.handle}`}
											legacyBehavior>
											<a
												className={(
													'u' +
													( active === collection ? ' active' : '' )
												)}
												onMouseOver={() => setActive( collection )}
												// onMouseOut={() => setActive( popular )}
											>
												<Title collection={collection} />
											</a>
										</Link>
									</motion.li>
								) )}
							</motion.ul>
						</div>
						{active?.product && (
							<motion.div
								className="popular"
								transition={{ duration: .5, ease }}
								variants={{
									hidden: { opacity: 0 },
									visible: { opacity: 1, transition: { delay: .8 } },
								}}
							>
								<small>{i.t( 'nav.collections.popular' )}</small>
								<Thumbnail product={active.product} />
							</motion.div>
						)}
					</motion.div>
					<motion.a
						aria-label="Close Menu"
						className="close"
						onClick={() => setFlyout( false )}
						transition={{ duration: .5, ease }}
						variants={{
							hidden: { opacity: 0 },
							visible: { opacity: 1, transition: { delay: 1 } },
						}}
					>
						<Close />
					</motion.a>
				</nav>
			</motion.div>
			<style jsx>{`
				.menu {
					position: relative;
					z-index: 3;
					pointer-events: ${( flyout === 'collections' ? 'auto' : 'none' )};
				}
				.menu > :global( div ) {
					position: fixed;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					z-index: 2;
				}

				nav {
					position: relative;
					height: 100%;
					width: 100%;
					max-width: 1000px;
					padding: 0 100px;
					display: flex;
					justify-content: center;
					align-items: center;
				}
				@media ( min-width: 760px ) {
					nav {
						width: 90%;
					}
				}
				@media ( min-width: 900px ) {
					nav {
						width: 85%;
					}
				}
				@media ( min-width: 1000px ) {
					nav {
						padding: 150px;
						width: 80%;
					}
				}
				@media ( min-width: 1300px ) {
					nav {
						padding: 150px 180px;
						width: 66%;
					}
				}
				nav :global( .background ) {
					position: absolute;
					top: 0;
					left: 0;
					right: 0;
					bottom: 0;
					background-color: white;
					box-shadow: ${( flyout === 'collections' ? '6px 2px 29px 0 rgba( 0, 0, 0, 0.15 )' : 'none' )};
					transition: ${( flyout === 'collections' ? 'none' : 'box-shadow .5s 1s' )};
					z-index: -1;
				}
				nav > :global( div ) {
					width: 100%;
					display: flex;
					justify-content: space-between;
					align-items: flex-start;
				}
				nav :global( ul a::after ) {
					height: 3px;
					left: 1px;
					right: 1px;
				}
				nav :global( .close ) {
					position: absolute;
					top: 56px;
					right: 56px;
					cursor: pointer;
				}
				nav :global( .collections ) {
					margin-top: 70px;
					font-size: 31px;
					line-height: 1.5;
					text-transform: lowercase;
				}
				@media ( min-width: 880px ) {
					nav :global( .collections ) {
						margin-left: -4px;
					}
				}
				nav :global( .collections li ) {
					margin-bottom: .8em;
				}
				@media ( max-width: ${mobileMenuMaxWidth}px ) {
					nav :global( div ) {
						width: 100%;
					}
					a.back {
						display: block;
						margin-bottom: 20px;
						font-size: 25px;
						text-transform: lowercase;
						cursor: pointer;
					}
					a.back :global( svg ) {
						margin-right: .7em;
					}
					nav :global( .collections ) {
						width: 100%;
						margin-top: 0px;
						display: grid;
						grid-gap: 9px;
						grid-template-columns: 1fr 1fr;
						grid-template-rows: auto;
					}
					nav :global( .collections li ) {
						margin: 0;
						padding: 0;
						font-size: 20px;
						background-color: #f7f7f7;
						text-align: center;
					}
					nav :global( .collections li a ) {
						width: 100%;
						height: calc( 40vw - 9px );
						display: flex;
						align-items: center;
						justify-content: center;
					}
					nav :global( ul a::after ) {
						height: 2px;
					}
				}
				nav :global( .popular ) {
					flex: 0 1 334px;
					margin-left: 50px;
				 	height: 550px;
					align-self: stretch;
					display: flex;
					flex-direction: column;
				}
				nav :global( .popular .rating ) {
					opacity: 1;
				}
				nav :global( .popular small ) {
					display: block;
					margin-bottom: 1em;
					font-size: 20px;
					line-height: 1.45;
				}
				nav :global( .popular a ) {
					flex: 1;
				}
				nav :global( .popular a article ) {
					height: 100%;
					display: flex;
					flex-direction: column;
				}
				nav :global( .popular a article picture ) {
					flex: 1 1 300px;
					min-height: 422px;
					padding-top: 25%;
					padding-bottom: 20%;
				}
				@media ( max-height: 850px ) {
					nav :global( .popular a article picture ) {
						flex-basis: 50vh;
						min-height: 0;
					}
				}
				@media ( max-width: ${mobileMenuMaxWidth}px ) {
					nav {
						width: 100%;
						padding: 10%;
						box-shadow: none;
					}
					a[aria-label="Ray Logo"],
					nav :global( .close ),
					nav :global( .popular ) {
						display: none;
					}
				}
			`}</style>
		</div>
	)
}
